import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy } from '@ngneat/until-destroy';
import { DayPilot, DayPilotSchedulerComponent } from "daypilot-pro-angular";
import { List } from 'immutable';
import * as moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import {
  activityAbbreviations,
  AdminEnum,
  arrivalStatus,
  BusEnum,
  ColorsEnum,
  departureStatus,
  EditModeEnum,
  EventTypes,
  GroupType
} from '../../common/enums';
import { ConfirmDialog } from '../../confirmation/confirm.dialog';
import { WorkShiftGroup } from '../../model/workShiftGroup';
import { Activity } from '../../model/activity';
import { ActivityActor } from '../../model/activityActor';
import { ActivityActorEvent } from '../../model/activityActor-event';
import { Actor, ActorMeta } from '../../model/actor';
import { Availability } from '../../model/availability';
import { LiveFlightEvent } from '../../model/liveflight-event';
import { Location } from '../../model/location';
import { StateMachineEventEnum } from '../../model/stateMachineEvent';
import { UserInfo } from '../../model/userInfo';
import { UserMapping } from '../../model/usermapping';
import { ActivityActorService } from '../../services/activityActor.service';
import { SettingsService } from '../../services/settings.service';
import { SignalRService } from "../../services/signalr.service";
import { TooltipService } from '../../services/toolTip.service';
import { DateTimeUtils } from '../../shared/datetimeutils';
import { EditEventDialogComponent } from '../edit-event-dialog/edit-event-dialog.component';
import { WorkShiftService } from '../../services/workShift.service';
import { UserMappingService } from '../../services/usermapping.service';
import { WorkShift } from '../../model/workShift';
import { AllowedEvents, SchedulerUpperService } from './scheduler-upper-service';
import { ActivityEvent } from './activityEvent';
import { ActivityDialogEvent } from './activityDialogEvent';
import { NewActivityObject } from './newActivityObject';
import { SchedulerUpperHelper } from './scheduler-upper-helper';
import { ResourceBase } from './resourceBase';
import { IResourceGroup, ResourceGroup } from './resourceGroup';
import { IResourceDriver, ResourceDriver } from './resourceDriver';
import { DaypilotCoreEvent, SupportedObjectTypes } from '../../model/daypilot-core-event';
import { LoggerService } from '../../services/logger.service';
import { LocationService } from '../../services/location.service';
import { ISchedulerMediatorServiceClient, SchedulerMediatorService } from '../scheduler-mediator.service';
import { guardSchedulerUpdates, isSchedulerMouseDown } from '../daypilot-update-guard';


@Component({
  selector: 'app-scheduler-upper',
  templateUrl: './scheduler-upper.component.html',
  styleUrls: ['.././scheduler.component.css', '.././areas.css', '.././scheduler_8.css', '.././navigator_white.css']
})

@UntilDestroy()
export class SchedulerUpperComponent implements AfterViewInit, OnInit, OnDestroy, ISchedulerMediatorServiceClient {

  @ViewChild("schedulerUpper", { static: true })
  schedulerUpper: DayPilotSchedulerComponent;

  @ViewChild('audioOption', { static: true })
  audioPlayerRef: ElementRef;

  /**
   * Constants
   **/
  readonly TimeChangeWarningThreshold = 10 * 60 * 1000;
  readonly UnknownGateStandId = "UNK_G";
  readonly UnknownGateStandType = "G";

  readonly ActivityActiveStatus = "ACTIVE";

  @Output()
  onPlannedActivities = new EventEmitter<number>();

  private releaseUpdateGuard: () => void = () => { };
  private _standbyExpanded: boolean = false;
  private _isRowMoving: boolean = false;
  private _pendingWorkShiftGroups: WorkShiftGroup[] | null = null;
  private _landsideExpanded: boolean = false;


  interaction: boolean = true;

  @Output()
  heightChanged = new EventEmitter<boolean>();

  _eventsUpper: ActivityActorEvent[] = [];

  get eventsUpper() {
    return this._eventsUpper;
  }

  set eventsUpper(value: ActivityActorEvent[]) {
    this._eventsUpper = value;
    ResourceDriver.invalidateBreakCache();
  }

  get liveFlightEvents(): LiveFlightEvent[] {
    return this.schedulerMediatorService.liveFlights;
  }

  activityActors: ActivityActorEvent[];
  workShiftGroups: WorkShiftGroup[] = [];
  workShifts: WorkShift[] = [];
  driversAirside: WorkShift[] = [];
  locations: Location[] = [];
  startLocation: Location
  endLocation: Location;
  userMappings: List<UserMapping> = List<UserMapping>();

  hasLiveFlights: boolean = false;
  hasActivities: boolean = false;

  startDate: DayPilot.Date;

  locationsSubscription: Subscription;
  availabilitySubscription: Subscription;
  worshiftGroupSubscription: Subscription;
  liveFlightSubscription: Subscription;
  busImportSubscription: Subscription;
  userMappingSubscription: Subscription;

  confirmModalRef: MatDialogRef<ConfirmDialog>;

  oddMinute: boolean = true;
  hideInfo: boolean = true;

  editEventModalRef: MatDialogRef<EditEventDialogComponent>;

  filter: any = {
    showFinishedEvents: false,
    showAllDrivers: false
  }

  configUpper: DayPilot.SchedulerConfig = {
    bubble: new DayPilot.Bubble({
      animation: "jump",
      showAfter: 100,
      hideAfter: 100,
      animated: false,
      showLoadingLabel: false,
      onLoad: (args) => {
        args.html = this.infoBubble(args);
        if (args.html)
          args.loaded();
      }
    }),
    onRowMoving: (args) => {
      this.onRowMoving(ResourceGroup.castToResourceGroup(args.target.data), ResourceDriver.castToResourceDriver(args.source.data), args);
    },
    onRowMoved: async (args) => {
      await this.onRowMoved(ResourceGroup.castToResourceGroup(args.target.data), ResourceDriver.castToResourceDriver(args.source.data), args);
    },
    onBeforeRowHeaderRender: (args) => {
      args.row.areas = this.getResourceAreas(args.row.data)
    },
    onResourceExpand: (args) => {
      if (args.resource.data?.type === GroupType.Standby) this._standbyExpanded = true;
      if (args.resource.data?.type === GroupType.Land) this._landsideExpanded = true;
      this.heightChanged.emit(true);
    },
    onResourceCollapse: (args) => {
      if (args.resource.data?.type === GroupType.Standby) this._standbyExpanded = false;
      if (args.resource.data?.type === GroupType.Land) this._landsideExpanded = false;
      this.heightChanged.emit(true);
    },
    onGridMouseDown: (args) => {
      if (args.originalEvent.button == 2) {
        args.action = "None";
      }
    },
    onBeforeTimeHeaderRender: (args) => {
      if (args.header.level == 0) {
        args.header.backColor = "#ffffff";
        args.header.cssClass = "timeHeader";
      } else if (args.header.level == 1) {
        args.header.backColor = "#f9f9f9";
        args.header.cssClass = "timeHeader";
      } else if (args.header.level == 2) {
        this.oddMinute = !this.oddMinute;
        if (this.oddMinute) {
          args.header.backColor = "#ffffff";
        } else {
          args.header.backColor = "#e9e9e9";
        }
      }
    },
    onBeforeCellRender: (args: any) => {
      if (ResourceDriver.isTimeForBreak(args.cell, args.cell.row.data, this.eventsUpper)) {
        args.cell.properties.backColor
        args.cell.properties.backColor = ColorsEnum.Break;
      } else if (ResourceDriver.shouldCellBeHighlightedDueToWorkingResource(args.cell, args.cell.row.data)) {
        args.cell.properties.backColor = ColorsEnum.Working;
      } else {
        args.cell.properties.backColor = ColorsEnum.NonWorking;
      }
    },
    onBeforeResHeaderRender: (args) => {
      this.onBeforeResHeaderRender(ResourceBase.castToResourceBase(args.resource), args);
    },
    onBeforeEventRender: (args) => {
      args.data.moveHDisabled = false;
      args.data.areas = this.getEventAreas(this.castToActivityActorEvent(args.data));
      args.data.resizeDisabled = false;
      args.data.fontColor = '#000000';
      args.data.barColor = this.activityStatusColor(this.castToActivityActorEvent(args.data));
      args.data.backColor = this.activityBgColor(this.castToActivityActorEvent(args.data));
    },
    onEventResize: async (args) => {
      await this.onEventResize(this.castToActivityActorEvent(args.e.data), args);
    },
    onEventResizing: (args) => {
      this.onEventResizing(this.castToActivityActorEvent(args.e.data), ResourceDriver.castToResourceDriver(args.row.data), args);
    },
    onEventMove: async (args) => {
      await this.onEventMoveFunction(this.castToActivityActorEvent(args.e.data), args);
    },
    onEventMoving: (args) => {
      this.onEventMoving(this.castToActivityActorEvent(args.e.data), args);
    },
    onEventRightClick: async (args) => {
      args.preventDefault();
      await this.onEventRightClickFunction(args);
    },
    onEventClicked: (args) => {
      this.onEventClickedFunction(this.castToActivityActorEvent(args.e.data));
    },
    onTimeRangeSelected: (args) => {
      this.onTimeRangeSelected(args);
    },
    onEventFilter: args => {
      args.visible = false;
      // User wants to show all events including finished ones
      if (args.filterParam.showFinishedEvents) {
        args.visible = true;
        return;
      }
      // Otherwise determine if the event is active or not
      let event: ActivityActorEvent = this.castToActivityActorEvent(args.e.data);
      if ((event.activityActor.status == this.ActivityActiveStatus) ||
        (event.activityActor.actor.metaExpanded.CancelledByApi &&
          !event.activityActor.actor.metaExpanded.CancelledByApiConfirmed)) {
        args.visible = true;
      }

    },
    onRowFilter: args => {
      args.visible = false;

      // User wants to show all drivers
      if (args.filterParam.showAllDrivers) {
        args.visible = true;

      } else {
        args.visible = this.isRowVisible(args.row);
      }

    },
  };

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly dialog: MatDialog,
    private readonly settings: SettingsService,
    private readonly tooltip: TooltipService,
    private readonly toastr: ToastrService,
    private readonly logger: LoggerService,
    private readonly signalRservice: SignalRService,
    private readonly activityActorService: ActivityActorService,
    private readonly workShiftService: WorkShiftService,
    private readonly usermappingService: UserMappingService,
    private readonly schedulerUpperService: SchedulerUpperService,
    private readonly schedulerUpperHelper: SchedulerUpperHelper,
    private readonly locationService: LocationService,
    private readonly schedulerMediatorService: SchedulerMediatorService) {

    this.schedulerUpperHelper.setupGeneralConfig(this.configUpper);

  }


  ngOnInit() {

    this.settings.toggleShowAllDrivers$.subscribe(item => {
      this.applyFilter();
    });

    this.settings.toggleShowFinishedEvents$.subscribe(item => {
      this.applyFilter();
    });

    this.userMappingSubscription = this.usermappingService.UserMappings.subscribe((result: List<UserMapping>) => {
      this.userMappings = result;
      ResourceDriver.setLoggedInStatusForAirsideDrivers(this.driversAirside, this.userMappings.toArray());
    });

    this.settings.toggleShowLandside$.subscribe(() => {
      this.createGroupsAndDrivers(this.workShiftGroups);
      this.applyFilter();
    });


  }

  ngAfterViewInit() {
    this.releaseUpdateGuard = guardSchedulerUpdates(this.schedulerUpper?.control);
    this.updateTimeLine(this.schedulerMediatorService.currentTimeLine);

    this.activityActorService.activityActorLoaded$.subscribe(
      () => {
        this.activityActors = this.activityActorService.activityActors;
        this.processActivityActors();
      });


    this.activityActorService.deleteActivity$.subscribe((activity: ActivityActorEvent) => {
      try {
        this.deleteActivity(activity);
      } catch (error) {
        this.logger.logError(error);
      }
    });

    this.locations = this.locationService.Locations;
    this.locationsSubscription = this.locationService.locationsLoaded$.subscribe((result: Location[]) => {
      this.locations = result;
    });


    // this is to setup all activities when switching between tabs. This needs to be done AFTER refreshLiveFlights
    if (!this.activityActors && this.activityActorService && this.activityActorService.activityActors) {
      this.activityActors = this.activityActorService.activityActors;
      this.processActivityActors();
    }



    this.signalRservice.logout$.subscribe((userInfo: UserInfo) => {
      this.setUserLoginStatus(userInfo.userId, false);
    });

    this.signalRservice.login$.subscribe((userInfo: UserInfo) => {
      this.setUserLoginStatus(userInfo.userId, true);

    });

    this.subscribeToWorkshifts();

    this.schedulerMediatorService.registerClient(this);

    this.refreshLiveFlights(this.liveFlightEvents);

    this.cdr.detectChanges();

    this.initPos();
  }

  ngOnDestroy() {
    this.schedulerMediatorService.unregisterClient(this);
    this.releaseUpdateGuard();
  }

  // mediator interface implementations start

  createLiveFlightEvents(liveFlights: LiveFlightEvent[]): void { /* intentionally left empty */ }
  updateLiveFlightEvents(liveFlights: LiveFlightEvent[]): void { /* intentionally left empty */ }
  deleteLiveFlightEvents(liveFlights: LiveFlightEvent[]): void { /* intentionally left empty */ }

  public onLiveFlightLoaded(liveFlights: LiveFlightEvent[]): void {  /* intentionally left empty */ }

  public updateActivityActorEvent(activityActorEvent: ActivityActorEvent): void {
    this.setupActivity(activityActorEvent);
    if (activityActorEvent.activityActor.state === StateMachineEventEnum.Finish) {
      this.playSound();
    }
  }

  public isRowMoving(): boolean {
    return this._isRowMoving;
  }

  public instanceName(): string {
    return "SchedulerUpper";
  }

  public updateTimeLine(timeLine: any[]): void {

    

    this.setTimeline(timeLine);
    this.clearHistory();
  }

  public updateNowMarker(date: DayPilot.Date) {

    this.updateTransferTimes();

    if (this.schedulerUpper.control)
      this.schedulerUpper.control.update({
        separators: [
          {
            color: "red",
            layer: "AboveEvents",
            location: date
          }
        ]
      });
  }

  public delayActionsDueToDropOfEvent() { /* intentionally left empty */ }

  // mediator interface implementations end

  public setTimeline(timeline: any[]) {

    this.configUpper.timeline = timeline;
    if (this.schedulerUpper?.control != null && this.schedulerUpper?.control != undefined) {
      this.schedulerUpper.control.update();
    }
  }

  public clearHistory() {
    let oldFlights = this.liveFlightEvents ? this.liveFlightEvents.filter(x => x.end.toDate() < this.settings.windowStart) : null;
    if (oldFlights) {
      oldFlights.forEach((flight: LiveFlightEvent) => {
        let flightIndex = this.liveFlightEvents.findIndex(x => x.id == flight.id);
        if (flightIndex >= 0) {
          this.liveFlightEvents.splice(flightIndex, 1);
        }
      });
    }

    let oldActivities = this.activityActors ? this.activityActors.filter(x => x.end.toDate() < this.settings.windowStart) : null;
    if (oldActivities) {
      oldActivities.forEach((activity) => {
        let activityIndex = this.activityActors.findIndex(x => x.activityActor.id == activity.activityActor.id);
        if (activityIndex >= 0) {
          this.activityActors.splice(activityIndex, 1);
        }
        let eventsIndex = this.eventsUpper.findIndex(x => x.activityActor.id == activity.activityActor.id);
        if (eventsIndex >= 0) {
          this.eventsUpper.splice(activityIndex, 1);
          ResourceDriver.invalidateBreakCache();
        }
      });
    }
  }

  private castToActivityActorEvent(arg: DayPilot.EventData): ActivityActorEvent {
    return arg as ActivityActorEvent;
  }

  // events start

  private setResourceGroupColors(resource: ResourceBase, args: DayPilot.SchedulerBeforeResHeaderRenderArgs) {
    if (resource.type == GroupType.Standby) {
      args.resource.backColor = "#FFA654";
      args.resource.fontColor = "#FFFFFF";
      return;
    }

    if (resource.type == GroupType.Air) {
      args.resource.backColor = "#5483B7";
      args.resource.fontColor = "#FFFFFF"
      return;
    }

    if (resource.type == GroupType.Land) {
      args.resource.backColor = "#7AB800";
      args.resource.fontColor = "#FFFFFF";
    }
  }

  private setResourceColors(resource: ResourceBase, args: DayPilot.SchedulerBeforeResHeaderRenderArgs) {
    if (resource.type == GroupType.Standby) {
      args.resource.backColor = "#FFD9A8";
      return;
    }

    if (resource.type == GroupType.Air) {
      args.resource.backColor = "#DDE9F6";
      return;
    }

    if (resource.type == GroupType.Land) {
      args.resource.backColor = "#DDF6E2";
    }
  }

  private onBeforeResHeaderRender(resource: ResourceBase, args: DayPilot.SchedulerBeforeResHeaderRenderArgs) {

    if (resource.isGroup) {
      this.setResourceGroupColors(resource, args);
    } else {
      this.setResourceColors(resource, args);
    }

    const isGroup = (value: any): value is IResourceGroup => {
      return !!value.children;

    }

    const isDriver = (value: any): value is IResourceDriver => {
      return !!value.userMappingID;

    }

    let visibleChildren = 0;
    let expanded = false;

    if (isGroup(resource)) {
      expanded = resource.expanded;
    }


    if ((expanded) || (!expanded && visibleChildren == 0)) {
      if (isDriver(resource) && (resource.type == GroupType.Air || resource.type == GroupType.Standby)) {
        resource.html = this.createDriverRowHtml(resource.workShift);
      } else {
        args.resource.html = '<div style="max-width: 110px; white-space:nowrap;overflow:hidden;text-overflow:ellipsis">' + resource.name + "</div>";
      }
    }
  }

  private createDriverRowHtml(workShift: WorkShift): string {
    let usermapping = this.userMappings.find(x => x.id == workShift.userMappingID);
    let userStatusIcon = ResourceDriver.lockIcon;
    if (usermapping !== undefined) {
      userStatusIcon = usermapping.loggedIn ? ResourceDriver.lockOpenIcon : ResourceDriver.lockIcon;
    }

    let htmlStringForIcon = '<div id="' + workShift.userMappingID + '" style="max-width: 140px; float:left">' + userStatusIcon + '</div>';
    return '<div style="max-width: 140px; display: flex; vertical-align: middle">' + htmlStringForIcon + '<div style="max-width: 140px; white-space:nowrap; overflow:hidden;text-overflow:ellipsis; margin: auto">' + workShift.name + "</div></div>";

  }

  private onRowMoving(targetGroup: ResourceGroup, sourceDriver: ResourceDriver, args) {

    if (!targetGroup || !sourceDriver) {
      args.position = "forbidden";
      return;
    }

    // no moving between air/land
    if (targetGroup.type === GroupType.Land || sourceDriver.type === GroupType.Land) {
      args.position = "forbidden";
      return;
    }

    if (!targetGroup.isGroup && (args.position === "child" || args.position === "forbidden")) {
      args.position = "after";
    }

    if (targetGroup.isGroup) {
      args.position = "child";
    }

  }

  private async onRowMoved(target: ResourceGroup, sourceDriver: ResourceDriver, args: DayPilot.SchedulerRowMovedArgs) {
    if (args.position === "forbidden" || !target || !sourceDriver) return;

    this.configUpper.rowMoveHandling = "Disabled";
    this._isRowMoving = true;
    try {
    const sourceGroup = this.workShiftGroups.find(x => x.type == sourceDriver.type);
    const targetGroup = this.workShiftGroups.find(x => x.type == target.type);

    if (!sourceGroup || !targetGroup) return;

    const sourceIndex = sourceGroup.workShifts.findIndex(x => x.userMappingID == this.extractUserMappingFromResourceKey(sourceDriver.id));
    const targetIndex = targetGroup.workShifts.findIndex(x => x.userMappingID == this.extractUserMappingFromResourceKey(target.id));
    const isSameGroup = sourceDriver.type === target.type;

    if (sourceIndex < 0) return;
    if (isSameGroup && sourceIndex === targetIndex) return;

    sourceGroup.workShifts.splice(sourceIndex, 1);

    if (sourceGroup.type === GroupType.Standby && sourceGroup.workShifts.length === 0) {
      this._standbyExpanded = false;
      setTimeout(() => this.schedulerUpper.control.rows.find(sourceGroup.id.toString())?.collapse());
    }

    this.insertDriverIntoGroup(targetGroup, sourceDriver, args.position, sourceIndex, targetIndex, isSameGroup);

    let displayOrder = 0;
    targetGroup.workShifts.forEach(ws => ws.displayOrder = displayOrder++);

    sourceDriver.type = target.type;
    await this.saveWorkShiftGroups();

    if (args.position == "child") {
      const shouldBeCollapsed = (target.type === GroupType.Standby && !this._standbyExpanded)
        || (target.type === GroupType.Land && !this._landsideExpanded);
      if (shouldBeCollapsed) {
        setTimeout(() => this.schedulerUpper.control.rows.find(target.id)?.collapse());
      }
    }
    } finally {
      this.configUpper.rowMoveHandling = "Update";
      this._isRowMoving = false;
      this.applyPendingWorkShiftGroups();
    }
  }

  private insertDriverIntoGroup(
    targetGroup: WorkShiftGroup,
    sourceDriver: ResourceDriver,
    position: string,
    sourceIndex: number,
    targetIndex: number,
    isSameGroup: boolean
  ) {
    if (position === "child") {
      targetGroup.workShifts.push(sourceDriver.workShift);
      return;
    }

    let insertIndex = targetIndex;

    if (position === "before") {
      if (isSameGroup && sourceIndex < targetIndex) {
        insertIndex--;
      }
    } else if (position === "after") {
      if (!isSameGroup || sourceIndex > targetIndex) {
        insertIndex++;
      }
    }

    targetGroup.workShifts.splice(insertIndex, 0, sourceDriver.workShift);
  }

  private async onEventClickedFunction(activityActorEvent: ActivityActorEvent) {
    try {
      let flight = activityActorEvent.IsFlight;
      let type = flight ? BusEnum.Air : activityActorEvent.activityActor.actor.type;
      if (type != BusEnum.Land) {
        let activity: Activity = new Activity({
          actorID: activityActorEvent.activityActor.actorID,
          id: activityActorEvent.id
        });

        let allowedEvents = await this.schedulerUpperService.getAllowedEvents(activity);

        let mode = EditModeEnum.Edit;

        this.handleExistingActivity(activityActorEvent, mode, allowedEvents);

      }
    } catch (error) {
      this.logger.logError(error);
    }
  }

  private async onEventResize(activityActorEvent: ActivityActorEvent, resizeArgs: DayPilot.SchedulerEventResizeArgs) {
    try {
      let prevStart = activityActorEvent.start;
      let prevEnd = activityActorEvent.end;

      //Floor the seconds to avoid rounding issue
      //Daypilot only accepts local time
      resizeArgs.newStart = new DayPilot.Date(resizeArgs.newStart.toString("yyyy-MM-ddTHH:mm:00"));
      resizeArgs.newEnd = new DayPilot.Date(resizeArgs.newEnd.toString("yyyy-MM-ddTHH:mm:00"));
      activityActorEvent.activityActor.start = resizeArgs.newStart.toDate();
      activityActorEvent.activityActor.duration = DateTimeUtils.calculateDurationMinutes(resizeArgs.newStart.toDate(), resizeArgs.newEnd.toDate());

      let act = new ActivityActor(activityActorEvent.activityActor);
      act.start = new Date(resizeArgs.newStart.toString("yyyy-MM-ddTHH:mm:00"));
      act.duration = DateTimeUtils.calculateDurationMinutes(resizeArgs.newStart.toDate(), resizeArgs.newEnd.toDate());

      let updatedActivity = await this.schedulerUpperService.updateActivity(act);

      if (updatedActivity === undefined) {
        resizeArgs.preventDefault();
        activityActorEvent.start = prevStart;
        activityActorEvent.end = prevEnd;
      }

    } catch (error) {
      this.logger.logError(error);
    }
  }

  private onEventResizing(activityActorEvent: ActivityActorEvent, resourceDriver: ResourceDriver, args: DayPilot.SchedulerEventResizingArgs) {
    try {
      args.left.html = "<div style='background-color:white;width:100%;height:100%'>" + moment(args.start.toDateLocal()).format('HH:mm') + "</div>";
      args.right.html = "<div style='background-color:white;width:100%;height:100%'>" + moment(args.end.toDateLocal()).format('HH:mm') + "</div>";

      args.allowed = args.duration.totalMinutes() > 3 && resourceDriver.workShift.isWorkingByTime(args.start.toDateLocal().getTime(), args.end.toDateLocal().getTime()) && this.isActivityInActiveState(activityActorEvent.activityActor);
    } catch (error) {
      this.logger.logError(error);

    }
  }

  private async onEventRightClickFunction(args: DayPilot.SchedulerEventRightClickArgs) {

    if (this.schedulerUpperHelper) {
      this.schedulerUpperHelper.hideMenu();
      await this.schedulerUpperHelper.createContextMenu(args, this.liveFlightEvents, this.schedulerUpper);
    }
  }

  private isActivityInActiveState(activityActor: ActivityActor) {
    return activityActor.state != StateMachineEventEnum.Finish &&
      activityActor.state != StateMachineEventEnum.Cancel &&
      activityActor.state != StateMachineEventEnum.Final &&
      activityActor.state != StateMachineEventEnum.NoShow;
  }

  private findDriverForResource(resource: string) {
    for (let group of this.workShiftGroups) {

      for (let workShift of group.workShifts) {
        let resourceKey = this.createResourceKey(group.type, workShift.userMappingID);

        if (resourceKey === resource) {
          return workShift;
        }
      }
    }
    return undefined;
  }

  private onEventMoving(activityActorEvent: ActivityActorEvent, args: DayPilot.SchedulerEventMovingArgs) {
    this.schedulerMediatorService.isMoving();
    try {

      let flight = args.e.data.liveFlight;

      let type = flight ? BusEnum.Air : activityActorEvent.activityActor.actor.type;

      if (flight || this.isActivityInActiveState(activityActorEvent.activityActor)) {
        args.right.enabled = false;
        args.allowed = type != BusEnum.Land && this.canEventBeMovedToResource(args as any);

        if (args.allowed) {
          let driver = this.findDriverForResource(args.resource as string);
          args.left.html = "<div style='background-color:white;width:100%;height:100%;'>" + driver.name + "</br>" + moment(args.start.toDateLocal()).format('HH:mm') + "</div>";
        }
      } else {
        args.allowed = false;
      }
    } catch (error) {
      this.logger.logError(error);
    }

  }

  private async onEventMoveFunction(activityActorEvent: ActivityActorEvent, args: DayPilot.SchedulerEventMoveArgs) {
    try {
      if (!this.interaction) return;

      args.preventDefault();

      if (activityActorEvent.objectType === "LiveFlightEvent" || activityActorEvent.resource === "transfers") {
        this.schedulerMediatorService.eventDropped();
      }

      if (args.e.data.liveFlight) {
        this.processMoveFlight(args.e.data, args);
        this.schedulerMediatorService.movingDone();
        return;
      }

      let existingActivity = this.getExistingActivity(activityActorEvent.activityActor.actor.type, args);

      if (!existingActivity) return;

      let activity: Activity = new Activity({
        actorID: existingActivity.activityActor.actorID,
        id: existingActivity.id,
      });

      existingActivity.activityActor.start = args.newStart ? args.newStart.toDateLocal() : args.e.data.start.toDateLocal();
      existingActivity.resource = args.newResource ? args.newResource : args.e.data.resource;

      existingActivity.start = args.newStart ? args.newStart : args.e.data.start;
      existingActivity.end = args.newEnd ? args.newEnd : args.e.data.end;

      let allowedEvents = await this.schedulerUpperService.getAllowedEvents(activity);
      this.processMove(args.ctrl, existingActivity, allowedEvents);


    } catch (error) {
      this.logger.logError(error);
    }

    this.schedulerMediatorService.movingDone();
  }

  private getExistingActivity(type: string, args: DayPilot.SchedulerEventMoveArgs): ActivityActorEvent {

    if (type == BusEnum.Transfer) {
      let existingActivity = args.e.data;

      if (!existingActivity) {
        existingActivity = this.eventsUpper.find(x => x.id == args.e.data.activityActor.id);
      }

      return existingActivity;
    }

    return this.eventsUpper.find(x => x.id == args.e.data.id);

  }

  private processMoveFlight(liveFlightEvent: LiveFlightEvent, args: DayPilot.SchedulerEventMoveArgs) {

    let activityEvent = this.createActivityEventForFlight(args.newResource.toString(), args.newStart, args.newEnd, liveFlightEvent);
    this.processActivity(activityEvent, null, '', true);
  }

  private processMove(ctrl: boolean, existingActivity: ActivityActorEvent, allowedEvents: AllowedEvents) {
    // If ctrl is pressed display dialog
    if (ctrl) {
      this.handleExistingActivity(existingActivity, EditModeEnum.Create, allowedEvents);
    } else {

      let activityEvent = this.createActivityEvent(existingActivity);
      this.processActivity(activityEvent, existingActivity, '', true);
    }
  }

  private infoBubble(args): string {
    if (args.source.data.activityActor.actor.type == BusEnum.Air) {
      let liveFlight =
        this.liveFlightEvents.find(x => x.liveFlight.id === args.source.data.activityActor.actor.liveFlightID);

      if (liveFlight) {
        return this.tooltip.createLiveFlightBubbleText(liveFlight,
          args.source.data.activityActor.actor,
          this.configUpper,
          args.source.data.activityActor);
      }

      return null;

    } else if ((args.source.data.activityActor.actor.type == BusEnum.Admin) ||
      (args.source.data.activityActor.actor.type == BusEnum.Misc) ||
      (args.source.data.activityActor.actor.type == BusEnum.Transfer)) {

      let location = args.source.data.activityActor.actor.type == BusEnum.Transfer ?
        this.locations.find(x => x.type == "T" && x.name == args.source.data.activityActor.startLocation.name) :
        null;

      return this.tooltip.createAdminBubbleText(args.source.data.activityActor, this.configUpper, location);

    } else
      return null;
  }

  private onTimeRangeSelected(args: DayPilot.SchedulerTimeRangeSelectedArgs) {
    try {
      args.control.clearSelection();
      if (this.interaction) {


        let actor = new Actor({
          name: "Admin",
          type: BusEnum.Admin,
          dateKey: moment(args.start.toDate()).format("YYYYMMDD"),
          liveFlightID: null,
          modifiedBy: "",
          airportID: this.schedulerUpperService.getCurrentAirport()
        });

        let actorMeta = new ActorMeta();
        actorMeta.Source = "KooWeb";

        actor.metaExpanded = actorMeta;

        let newActivityObject = new NewActivityObject(
          args.start,
          args.resource.toString(),
          BusEnum.Admin,
          this.schedulerUpperHelper.diff_minutes(args.end.toDate(), args.start.toDate()),
          actor
        );

        if (newActivityObject.duration >= 5) {
          this.handleNewActivity(newActivityObject, EditModeEnum.Create, new AllowedEvents(null));
        }
      }
    } catch (error) {
      this.logger.logError(error);

    }
  }

  // events end

  // scheduler helper methods

  private enableInteraction() {
    setTimeout(() => {
      this.interaction = true;
    }, 1000);
  }

  private isRowVisible(row: DayPilot.Row): boolean {

    let result: boolean = false;
    let rowData = row.data;
    // Row has children? It's a container branch so always show it
    if ((rowData.children) || (rowData.isGroup)) {
      result = true;

    } else {
      let events = row.events.all();
      let active = events.length > 0 ? events.find(x => x.data.activityActor.status == this.ActivityActiveStatus) : null;

      let workShift: WorkShift = rowData.workShift;
      if (workShift.isWorking(new Date()) || active) {
        // Show the row if the current time is within the driver's planned work shift
        // or has active events
        result = true;
      }
    }

    return result;
  }

  private getActor(event: any, type: string): Actor {
    try {
      let actorMeta = new ActorMeta();
      actorMeta.Source = "KooWeb";

      let actor = new Actor({
        name: event.text,
        type: type,
        dateKey: moment(event.start.toDateLocal()).format("YYYYMMDD"),
        liveFlightID: event.liveFlight ? event.id : event.activityActor.actor.liveFlightID,
        modifiedBy: "",
        description: this.getDescriptionForActorInEvent(event),
        airportID: this.schedulerUpperService.getCurrentAirport()
      });

      actor.metaExpanded = actorMeta;
      return actor;
    } catch (error) {
      this.logger.logError(error);
    }
  }

  private getDescriptionForActorInEvent(event: DaypilotCoreEvent): string {

    if (event.objectType === SupportedObjectTypes.LiveFlightEvent) return "";

    if (event.objectType === SupportedObjectTypes.ActivityActorEvent) {
      let activityActorEvent: ActivityActorEvent = event as ActivityActorEvent;
      return activityActorEvent.activityActor.actor.description;
    }

    return "";
  }

  // scheduler helper methods end

  private refreshLiveFlights(result: LiveFlightEvent[]) {
    try {
      if (this.activityActors && this.activityActors.length > 0) {
        this.processActivityActors();
      }

    } catch (error) {
      this.logger.logError(error);
    }
  }

  private async saveWorkShiftGroups() {
    try {
      await this.workShiftService.setWorkShiftGroups(this.workShiftGroups);
      return true;
    } catch (error) {
      this.showError("Ett fel uppstod vid uppdateringen!");
      console.log("Something went wrong while updating activity:", error);
      this.workShiftService.readWorkShifts(new Date());
      return false;
    }

  }

  private processActivityActors() {
    try {
      this.createActivityActors();

      // Apply the filter to the newly created events
      this.applyFilter();
      this.countPlannedActivities();
    } catch (error) {
      this.logger.logError(error);
    }
  }

  private countPlannedActivities() {
    let planned = this.eventsUpper.filter(v => v.activityActor.state == "Planned").length;
    this.onPlannedActivities.emit(planned);
  }

  private setUserLoginStatus(id: string, loggedin: boolean) {
    try {

      let driverResourceKey = this.createResourceKey(GroupType.Air, id);
      let driver = ResourceDriver.getDriverFromDayPilotResourceByUserMappingId(driverResourceKey, this.configUpper.resources);

      if (driver) {
        this.updateUserMappingListWithLoggedIn(id, loggedin);
      }

      if (this.schedulerUpper && this.schedulerUpper.control) {
        this.schedulerUpper.control.update();
      }
    } catch (error) {
      this.logger.logError(error);
    }
  }

  private updateUserMappingListWithLoggedIn(id: string, loggedin: boolean) {
    let userToUpdate = this.userMappings.find(x => x.id == id);
    let index = this.userMappings.findIndex(x => x.id == id);
    userToUpdate.loggedIn = loggedin;
    this.userMappings.splice(index, 1);
    this.userMappings.push(new UserMapping(userToUpdate));
  }

  private playSound() {
    if (this.settings.sound) {
      this.audioPlayerRef.nativeElement.play();
    }
  }

  private subscribeToWorkshifts() {
    if (this.workShiftService.workShiftGroups.length > 0) {
      this.updateWorkShiftGroups(this.workShiftService.workShiftGroups);
    }

    if (!this.worshiftGroupSubscription) {
      this.worshiftGroupSubscription = this.workShiftService.workShiftGroupsUpdated$.subscribe(() => {
        this.updateWorkShiftGroups(this.workShiftService.workShiftGroups);
      });
    }

  }

  private applyPendingWorkShiftGroups() {
    if (!this._pendingWorkShiftGroups) return;
    const groups = this._pendingWorkShiftGroups;
    this._pendingWorkShiftGroups = null;
    this.updateWorkShiftGroups(groups);
  }

  private updateWorkShiftGroups(groups: WorkShiftGroup[]) {
    if (this._isRowMoving || isSchedulerMouseDown()) {
      this._pendingWorkShiftGroups = groups;
      // the guard only flushes control.update, so retry here once the mouse is released
      setTimeout(() => this.applyPendingWorkShiftGroups(), 100);
      return;
    }

    try {

      const windowStart: Date = this.settings.windowStart;
      const windowEnd: Date = this.settings.windowEnd;

      this.workShiftGroups = groups;

      this.workShiftGroups.forEach(group => {
        group.workShifts.forEach(workShift => {

          workShift.availabilities = workShift.availabilities.filter(f => f.isWorkingWithinTimespan(windowStart, windowEnd));
        });

        group.workShifts = group.workShifts.filter(f => f.isWorkingWithinTimespan(windowStart, windowEnd));

      });

      this.createGroupsAndDrivers(this.workShiftGroups);

      this.applyFilter();
      this.initPos();
      this.heightChanged.emit(true);

    } catch (error) {
      this.logger.logError(error);
    }

  }

  private initPos() {
    if (this.schedulerUpper?.control) {
      this.schedulerUpper.control.scrollTo(new DayPilot.Date().addHours(-1));
    }
  }

  private createResourceKey(groupType: string, userMappingId: string): string {

    if (groupType === GroupType.Air || groupType === GroupType.Standby) {
      groupType = GroupType.Air;
    }

    return `${groupType}|${userMappingId}`;
  }

  private extractUserMappingFromResourceKey(resourceKey: string): string {
    return resourceKey.split("|")[1];
  }

  private createGroupsAndDrivers(groups: WorkShiftGroup[]) {

    // clear previous entries
    this.driversAirside = [];
    this.configUpper.resources = [];

    groups.forEach(group => {

      if (group.type === GroupType.Coordinator || group.type === GroupType.Other) {
        return;
      }

      // If standby/landside has no rows, force collapse and reset the expanded flag
      if (group.type === GroupType.Standby && group.workShifts.length === 0) {
        this._standbyExpanded = false;
      }
      if (group.type === GroupType.Land && group.workShifts.length === 0) {
        this._landsideExpanded = false;
      }

      // standby and land should never be expaned on reload (F5)
      let expanded = group.workShifts.length > 0 && group.type !== GroupType.Standby && group.type !== GroupType.Land;

      // allow standby group to be expanded if user explicitly expanded it
      if (group.type === GroupType.Standby && this._standbyExpanded) {
        expanded = true;
      }

      // allow landside to be expanded if user explicitly expanded it
      if (group.type === GroupType.Land && this._landsideExpanded) {
        expanded = true;
      }

      let newGroup: ResourceGroup = new ResourceGroup(
        group.id.toString(),
        true,
        group.type,
        true,
        '',
        group.name,
        expanded,
        null,
        group.workShifts.length
      );

      group.workShifts.forEach(workShift => {
        let driver: ResourceDriver = new ResourceDriver(
          this.createResourceKey(group.type, workShift.userMappingID),
          false,
          group.type,
          newGroup.type == GroupType.Land,
          '',
          workShift.name,
          workShift.userMappingID,
          newGroup.id,
          false,
          workShift);


        if ((group.type == GroupType.Air) || (group.type == GroupType.Standby)) {
          this.driversAirside.push(workShift);
        }
        if (!newGroup.children) {
          newGroup.children = [];
        }
        newGroup.children.push(driver);
      });

      if ((group.type != GroupType.Land) || (group.type == GroupType.Land && this.settings.showLandside)) {
        this.configUpper.resources.push(newGroup);
      }
    });

  }

  private getEventAreas(event: ActivityActorEvent) {
    try {
      let areas = [];
      let icon: string;
      let showIcon: boolean;

      let duration = DateTimeUtils.calculateDurationNumber(event.start.toDate(), event.end.toDate());

      // Display correct icon for flights
      if (event.activityActor.actor.type == BusEnum.Air) {

        icon = this.getIconForAir(event);
        showIcon = duration > 23;

      } else if (event.activityActor.actor.type == BusEnum.Transfer) {

        icon = "directions_bus";
        areas.push(this.getEventAreaForTransfer(event));
        showIcon = duration > 14;

      } else {

        let type = EventTypes.find(x => x.id == event.activityActor.actor.type
          && x.name == event.activityActor.actor.name);

        if (type) {
          icon = type.icon;
        }

        showIcon = duration > 0;
      }


      if (showIcon || !this.hideInfo) {
        let html = this.getEventAreaIconHtml(event, icon);
        areas.push({ html: html, right: 5, top: 8, visibility: "Visible" });
      }

      return areas;

    } catch (error) {
      this.logger.logError(error);
    }
  }

  private getIconForAir(event: ActivityActorEvent): string {
    let arrival = event.activityActor.actor.liveFlightID.endsWith("_A");

    if (arrival) {
      return "arrow_downward";
    }

    return "arrow_upward";
  }

  private getEventAreaIconHtml(event: ActivityActorEvent, icon: string) {

    let eventType = EventTypes.find(x => x.id == event.activityActor.actor.type
      && x.name == event.activityActor.actor.name);

    let blink = event.activityActor.state == StateMachineEventEnum.Finish;

    if (blink && eventType && eventType.isSvgIcon) {
      return '<img style="animation: blink 2s ease-in infinite" src="../../../assets/images/' + icon + '.svg" alt=""></img>';
    }

    if (blink) {
      return "<i class='material-icons' style='animation: blink 2s ease-in infinite'>" + icon + "</i>";
    }

    if (eventType?.isSvgIcon) {
      return '<img src="../../../assets/images/' + icon + '.svg" alt=""></img>';
    }

    return "<i class='material-icons'>" + icon + "</i>";

  }

  private getEventAreaForTransfer(event: ActivityActorEvent) {
    let abb = activityAbbreviations.find(x => x.state == event.activityActor.state);

    if (abb.eta) {

      if (event.activityActor.actor.etaTransfer === null || event.activityActor.actor.etaTransfer === undefined) {
        return {
          html: "<div><b>?</b></div>",
          right: 28,
          top: 13,
          visibility: "Visible"
        };

      }
      let estimated = moment(event.activityActor.actor.etaUpdated);
      estimated = estimated.add(event.activityActor.actor.etaTransfer, 'minutes');
      let now = moment(new Date());
      let remainingMinutes = moment.duration(estimated.diff(now)).asMinutes().toFixed(0);
      let diff = (estimated > now && remainingMinutes != "0") ? remainingMinutes : "1";
      let color = (estimated > now && remainingMinutes != "0") ? "black" : "red";

      return {
        html: "<div style='color:" + color + "'><b>" + diff + "</b></div>",
        right: 28,
        top: 13,
        visibility: "Visible"
      };

    }

    return {
      html: "",
      right: 28,
      top: 13,
      visibility: "Visible"
    };

  }

  private updateTransferTimes() {

    let transferEvents = this.eventsUpper.filter(e => e.activityActor.actor.type === BusEnum.Transfer);
    transferEvents.forEach(e => {
      let newDate = e.start.addMilliseconds(1);
      e.start = newDate as any;
    });

    // only redraw if events exists
    if (transferEvents.length > 0 && this.schedulerUpper.control)
      this.schedulerUpper.control.update();

  }

  private getResourceAreas(data) {
    try {
      let areas: any[] = [];
      if (data && !data.isGroup) {

        let workShift: WorkShift = data.workShift;
        let bus = "<div class='h-100 d-flex align-items-center'><span>" + workShift.getBusForCurrentAvailability() + "</span></div>";
        let busArea = { html: bus, right: 5, visibility: "Visible", height: "100%" };
        areas.push(busArea);
      }

      return areas;
    } catch (error) {
      this.logger.logError(error);
    }
  }

  private createActivityActors() {
    try {
      this.eventsUpper = [];
      this.activityActors.filter(a => a.planned).forEach(event => {
        this.setupActivityActorEvent(event);
        this.eventsUpper.push(event);
      });
      ResourceDriver.invalidateBreakCache();
    } catch (error) {
      this.logger.logError(error);
    }
  }

  private getEventEnd(event: ActivityActorEvent): DayPilot.Date {

    let eventEnd = event.activityActor.end;

    if (event.activityActor.ended &&
      (event.activityActor.state == StateMachineEventEnum.Final ||
        event.activityActor.state == StateMachineEventEnum.Finish ||
        event.activityActor.state == StateMachineEventEnum.Cancel)) {
      eventEnd = event.activityActor.ended;
    }

    let eventMin = moment(event.activityActor.start).add(10, 'm').toDate();
    if (eventEnd < eventMin) {
      return this.schedulerUpperHelper.toDayPilotDate(eventMin);
    }

    return this.schedulerUpperHelper.toDayPilotDate(eventEnd);

  }

  private createActivityEvent(activityActorEvent: ActivityActorEvent): ActivityEvent {

    let toBeBilled: boolean = false;
    if (activityActorEvent) {
      let tempActor = new Actor(activityActorEvent.activityActor.actor);
      toBeBilled = tempActor.metaExpanded.ToBeBilled;
    }

    let startLocation = activityActorEvent.activityActor.startLocation;
    let endLocation = activityActorEvent.activityActor.endLocation;

    let activityEvent = new ActivityEvent(
      false,
      false,
      activityActorEvent ? activityActorEvent.activityActor.comment : null,
      startLocation ? startLocation.id : null,
      endLocation ? endLocation.id : null,
      activityActorEvent.resource,
      this.extractUserMappingFromResourceKey(activityActorEvent.resource),
      moment(activityActorEvent.activityActor.start).format("HH:mm"),
      activityActorEvent ? this.schedulerUpperHelper.toDayPilotDate(activityActorEvent.activityActor.start) : null,
      activityActorEvent ? this.schedulerUpperHelper.diff_minutes(activityActorEvent.activityActor.end, activityActorEvent.activityActor.start) : null,
      toBeBilled,
      activityActorEvent ? activityActorEvent.activityActor.actor : null,
      activityActorEvent ? activityActorEvent.activityActor.actor.type : null
    );

    return activityEvent;
  }

  private createActivityEventForFlight(resourceId: string, start: DayPilot.Date, end: DayPilot.Date, liveFlightEvent: LiveFlightEvent): ActivityEvent {

    let startLocation = this.startLocation ? this.startLocation.id : null;
    let endLocation = this.endLocation ? this.endLocation.id : null;

    let activityEvent = new ActivityEvent(
      false,
      false,
      liveFlightEvent.liveFlight.comment,
      startLocation,
      endLocation,
      resourceId,
      this.extractUserMappingFromResourceKey(resourceId),
      moment(start.toDateLocal()).format("HH:mm"),
      start,
      this.schedulerUpperHelper.diff_minutes(end.toDateLocal(), start.toDateLocal()),
      false,
      this.getActor(liveFlightEvent, BusEnum.Air),
      BusEnum.Air
    );

    return activityEvent;
  }

  private createActivityEventForNewActivity(newActivity: NewActivityObject): ActivityEvent {
    let activityEvent = new ActivityEvent(
      false,
      false,
      null,
      null,
      null,
      newActivity.resourceId,
      this.extractUserMappingFromResourceKey(newActivity.resourceId),
      "",
      newActivity.start,
      newActivity.duration,
      false,
      newActivity.actor,
      newActivity.actor.type
    );

    return activityEvent;
  }

  private handleNewActivity(newActivity: NewActivityObject, mode: string, allowedEvents: AllowedEvents) {

    let activityEvent = this.createActivityEventForNewActivity(newActivity);
    this.handleActivityInternal(activityEvent, null, mode, allowedEvents);
  }

  private handleExistingActivity(existingActivityActorEvent: ActivityActorEvent, mode: string, allowedEvents: AllowedEvents) {

    let activityEvent = this.createActivityEvent(existingActivityActorEvent);
    this.handleActivityInternal(activityEvent, existingActivityActorEvent, mode, allowedEvents);
  }

  private handleActivityInternal(activityEvent: ActivityEvent, activityActorEvent: ActivityActorEvent, mode: string, allowedEvents: AllowedEvents) {

    let activityDialogEvent = new ActivityDialogEvent(
      activityEvent,
      "",
      !!activityActorEvent,
      this.locations,
      activityActorEvent ? this.schedulerUpperHelper.toDayPilotDate(activityActorEvent.activityActor.end) : null,
      mode,
      activityActorEvent ? activityActorEvent.activityActor.state : "",
      activityActorEvent ? activityActorEvent.activityActor : null,
      allowedEvents.UnAssign(),
      allowedEvents.Cancel(),
      this.workShiftService.getWorkShiftsForAir(),
    );

    // set this to prevent simultaneous range select event
    this.interaction = false;
    this.editEventModalRef = this.dialog.open(EditEventDialogComponent,
      {
        width: '465px',
        disableClose: false,
        panelClass: 'eventsDialog',
        closeOnNavigation: false,
        data: activityDialogEvent
      });

    this.editEventModalRef.keydownEvents().subscribe(result => {
      if (result.key == "Escape") {
        this.enableInteraction();
      }
    });

    this.editEventModalRef.backdropClick().subscribe(() => {
      this.enableInteraction();
    });

    this.editEventModalRef.afterClosed().subscribe((result: ActivityDialogEvent) => {
      if (!result) {
        this.enableInteraction();
        return;
      }
      this.processActivity(result?.activityEvent, activityActorEvent, result.command, false);
    });
  }

  private setupActivity(event: ActivityActorEvent) {

    if (this.schedulerUpper?.control?.events === null || this.schedulerUpper?.control?.events === undefined) {
      return;
    }

    try {
      if (event.activityActor.status == this.ActivityActiveStatus ||
        event.activityActor.state == StateMachineEventEnum.Final ||
        event.activityActor.state == StateMachineEventEnum.NoShow) {

        this.setupActivityActorEvent(event);

        let index = this.eventsUpper.findIndex(x => x.activityActor.id == event.activityActor.id);

        if (index >= 0) {
          // update
          this.eventsUpper.splice(index, 1, event);
        } else {
          // add
          this.schedulerUpper.control.events.add(event as any);
        }
        ResourceDriver.invalidateBreakCache();
        this.schedulerUpper.control.update();
        this.countPlannedActivities();
        this.applyFilter();

      } else if (event.activityActor.status == "CLOSED") {
        this.deleteActivity(event);
      }
    } catch (error) {
      this.logger.logError(error);
    }
  }

  private setupActivityAirsideEventHtml(event: ActivityActorEvent) {

    let color = ColorsEnum.ActivityTextColor;

    if (!this.liveFlightEvents) {
      console.log("LiveFlightEvents not ready yet");
      return;
    }

    let lfevent = this.liveFlightEvents.find(x => x.liveFlight.id === event.activityActor.actor.liveFlightID);

    if (!lfevent) this.logger.logEvent("SetupActivityActor", "Could not find " + event.activityActor.actor.liveFlightID);

    let departure = event.activityActor.actor.liveFlightID.endsWith('_D');
    let arrival = event.activityActor.actor.liveFlightID.endsWith('_A');

    let statusText: string = ' ST:?';

    if (lfevent && arrival) {
      statusText = " ST:" + arrivalStatus(lfevent.liveFlight.arrivalStatusExpanded.flightLegStatus.toString().toUpperCase());
    } else if (lfevent && departure) {
      statusText = " ST:" + departureStatus(lfevent.liveFlight.departureStatusExpanded.gateAction);
    }

    let startLocation: string = "S:? ";
    let endLocation: string = "G:?";

    if (departure) {
      startLocation = "G:? ";
      endLocation = "S:?";
    }

    let text = event.activityActor.actor.name + statusText + "<br/>";

    if (event.activityActor.startLocation) {
      startLocation = event.activityActor.startLocation.type + ":" + event.activityActor.startLocation.name + "  ";
    }

    if (event.activityActor.endLocation) {
      endLocation = event.activityActor.endLocation.type + ":" + event.activityActor.endLocation.name;
    }

    let pax = lfevent ? this.tooltip.getNumberOfPassengers(lfevent.liveFlight) : "?";
    let paxText: string = "P:" + pax;

    text += startLocation + endLocation + "<br/>";
    text += paxText;

    event.height = 48;

    if (lfevent && lfevent.liveFlight.acknowledge) {
      color = ColorsEnum.ActivityChangedTextColor;
    }

    event.html = "<div style='color:" +
      color +
      ";margin-top:4px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis'>" +
      text +
      "</div>";

  }

  private setupTransferEventHtml(event: ActivityActorEvent) {

    let text: string = event.activityActor.actor.name;
    event.html = "<div style='color:" +
      ColorsEnum.ActivityTextColor +
      ";margin-top:4px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis'>" +
      text +
      "</div>";
  }

  private setupActivityOtherEventHtml(event: ActivityActorEvent) {

    let eventType = EventTypes.find(x => x.id == event.activityActor.actor.type && x.name == event.activityActor.actor.name);

    let text: string = this.getEventTextForOtherEventTypes(event, eventType);

    let start = event.activityActor.startLocation ? "S:" + event.activityActor.startLocation.name : "";
    let end = event.activityActor.endLocation ? "D:" + event.activityActor.endLocation.name : "";

    if (start != "" || end != "") {
      text += "<br/>" + start + (start != "" ? "  " : "") + end;
    }

    event.html = "<div style='color:" +
      ColorsEnum.ActivityTextColor +
      ";margin-top:4px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:calc(100% - 27px)'>" +
      text +
      "</div>";

  }

  private setupActivityLandEventHtml(event: ActivityActorEvent) {

    let text: string = event.activityActor.actor.name +
      "&nbsp;&nbsp;&nbsp;&nbsp;" +
      moment(event.activityActor.start).format("HH:mm") +
      "&nbsp;&ndash;&nbsp;" +
      moment(event.activityActor.end).format("HH:mm");

    event.html = "<div style='color:" +
      ColorsEnum.ActivityTextColor +
      ";margin-top:4px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis'>" +
      text +
      "</div>";
  }

  private setupActivityActorEvent(event: ActivityActorEvent) {

    try {

      if (!event.activityActor.actor.type) {
        throw new Error("Actor missing type.");
      }

      let availability = this.workShiftService.findAvailability(event.activityActor.availabilityID);

      if (availability !== undefined) {

        let groupType: string = GroupType.Air;
        if (availability.isLand) {
          groupType = GroupType.Land;
        }

        event.resource = this.createResourceKey(groupType, availability.userMappingID);
      } else {
        event.resource = null;
      }

      event.end = this.getEventEnd(event);

      switch (event.activityActor.actor.type) {
        case BusEnum.Air:
          this.setupActivityAirsideEventHtml(event);
          return;
        case BusEnum.Transfer:
          this.setupTransferEventHtml(event);
          return;
        case BusEnum.Admin:
        case BusEnum.Misc:
          this.setupActivityOtherEventHtml(event);
          return;
        case BusEnum.Land:
          this.setupActivityLandEventHtml(event);
          return;
      }


    } catch (error) {
      this.logger.logError(error);

    }
  }

  private isFlightTypeWithDescription(event: ActivityActorEvent, eventType: any) {
    return eventType.name === AdminEnum.Flight &&
      event.activityActor.actor.description !== undefined &&
      event.activityActor.actor.description !== "";
  }

  private getEventTextForOtherEventTypes(event: ActivityActorEvent, eventType: any) {

    let duration = DateTimeUtils.calculateDurationNumber(event.start.toDate(), event.end.toDate());
    let text: string = event.activityActor.actor.name;

    if (eventType) {
      if (eventType.name !== AdminEnum.Flight) {
        text = duration >= eventType.textLength ? eventType.displayText : "";
      }

      if (this.isFlightTypeWithDescription(event, eventType)) {
        text = event.activityActor.actor.name + " (" + event.activityActor.actor.description.toUpperCase() + ")";
      }
    }

    return text;
  }

  private async createActivity(actor: Actor, activity: Activity, availability: Availability) {

    if (activity.state === BusEnum.Transfer) {
      this.createBusTransferInternal(activity, availability, actor);
      return;
    }

    this.createActivityInternal(actor, activity, availability);

  }

  private async createActivityInternal(actor: Actor, activity: Activity, availability: Availability) {
    let newActivity = await this.schedulerUpperService.updateState("Create", actor, activity);

    if (newActivity === undefined) {
      return;
    }

    newActivity.userMappingID = availability.userMappingID;
    newActivity.availabilityID = availability.id;

    let createStatus = StateMachineEventEnum.Planned;

    if (activity.state === BusEnum.Admin) {
      createStatus = StateMachineEventEnum.Assign;
      let event = EventTypes.find(x => x.name === actor.name);
      if (event)
        createStatus = event.state;
    }

    let result = await this.schedulerUpperService.updateState(createStatus, actor, newActivity);
    if (result === undefined) {
      return;
    }

    this.showInfo(createStatus === StateMachineEventEnum.Assign
      ? 'Uppdraget har blivit tilldelat.'
      : 'Uppdraget har blivit plannerat.');
  }

  private async createBusTransferInternal(activity: Activity, availability: Availability, actor: Actor) {
    // only assign is needed for transfers
    activity.userMappingID = availability.userMappingID;
    activity.availabilityID = availability.id;
    activity.actorID = actor.id;

    await this.schedulerUpperService.updateState("Assign", actor, activity);
  }

  private deleteActivity(event: ActivityActorEvent) {
    try {
      let index = this.eventsUpper.findIndex(x => x.activityActor.id == event.activityActor.id);

      if (index >= 0) {
        // delete
        this.eventsUpper.splice(index, 1);
        ResourceDriver.invalidateBreakCache();

        if (this.schedulerUpper.control) {
          this.schedulerUpper.control.update();
        }
      }

      this.countPlannedActivities();
    } catch (error) {
      this.logger.logError(error);
    }
  }

  private async proccessTransferActivity(activityEvent: ActivityEvent, existingActivity: ActivityActorEvent, availability: Availability, hasBeenMoved: boolean) {

    let date = this.schedulerUpperHelper.parseTime(activityEvent.newStart, activityEvent.startTime);
    let hasNewResource = existingActivity.activityActor.userMappingID != activityEvent.selectedUserMappingId;

    existingActivity.activityActor.userMappingID = availability.userMappingID;
    existingActivity.activityActor.availabilityID = availability.id;
    existingActivity.activityActor.start = date;
    existingActivity.activityActor.comment = activityEvent.comment;
    existingActivity.activityActor.duration = DateTimeUtils.getDurationString(activityEvent.duration);

    if (activityEvent.dirtyActivity || hasBeenMoved) {

      if (hasNewResource) {
        this.reassignActivity(existingActivity.activityActor, activityEvent.selectedUserMappingId);
        this.showInfo('Uppdraget har blivit uppdaterat.');
        return true;
      } else {
        let updatedActivity = await this.schedulerUpperService.updateActivity(existingActivity.activityActor);

        return updatedActivity !== undefined;
      }
    }
  }

  private async processActivity(activityEvent: ActivityEvent, existingActivity: ActivityActorEvent, command: string, moved: boolean) {

    if (!activityEvent) {
      this.enableInteraction();
      return;
    }

    // Fix for simultaneous raneg select event, no range select will be allowed for 1 second
    this.enableInteraction();

    let workShiftForDriver = this.findDriverForResource(activityEvent.selectedResourceId);

    if (!workShiftForDriver) {
      console.log("Cannot find workshift for driver: " + activityEvent.selectedResourceId);
      return;
    }


    let date = this.schedulerUpperHelper.parseTime(activityEvent.newStart, activityEvent.startTime);

    let matchingAvailabilities = workShiftForDriver.availabilities.filter(a => a.start <= date && a.end >= date);
    if (matchingAvailabilities.length !== 1) {
      console.log("Could not find availability");
      return;
    }

    let availability = matchingAvailabilities[0];

    let actor = this.createActorFromActivityEvent(activityEvent)
    let activity = this.createActivityFromActivityEvent(activityEvent, date);

    if (!existingActivity) {
      await this.createActivity(actor, activity, availability);
      return;
    }

    if (existingActivity.activityActor.actor.type === BusEnum.Transfer) {
      if (command === "cancelActivity") {
        await this.schedulerUpperService.cancelActivity(actor, existingActivity.activityActor);
        return;
      }

      await this.proccessTransferActivity(activityEvent, existingActivity, availability, moved);
      return;
    }

    if (command == "unassign") {
      await this.unassignActivity(actor, existingActivity.activityActor);
      return;
    } else if (command === "cancelActivity") {
      await this.schedulerUpperService.cancelActivity(actor, existingActivity.activityActor);
      return;
    }

    let hasNewResource = existingActivity.activityActor.userMappingID != activityEvent.selectedUserMappingId;
    this.updateActivityFromActivityEvent(existingActivity, activityEvent, actor, availability.userMappingID, date);


    if (activityEvent.dirtyActor) {
      let updateResult = await this.schedulerUpperService.updateActor(existingActivity.activityActor.actor);
      if (!updateResult) return;
    }

    if ((activityEvent.dirtyActivity || moved) && hasNewResource) {
      await this.reassignActivity(existingActivity.activityActor, activityEvent.selectedUserMappingId);
      return;
    } else if (activityEvent.dirtyActivity || moved) {
      await this.updateActivity(existingActivity.activityActor, hasNewResource ? activityEvent.selectedUserMappingId : null);
      return;
    }
    if (!activityEvent.dirtyActivity && !moved && !hasNewResource) {
      this.showInfo("Uppdraget har uppdaterats.");
    }

  }

  private updateActivityFromActivityEvent(existingActivity: ActivityActorEvent, activityEvent: ActivityEvent, actor: Actor, mappingId: string, date: Date) {
    existingActivity.activityActor.startLocationID = activityEvent.startLocation;
    existingActivity.activityActor.endLocationID = activityEvent.endLocation;
    existingActivity.activityActor.start = date;
    existingActivity.activityActor.comment = activityEvent.comment;
    existingActivity.activityActor.duration = DateTimeUtils.getDurationString(activityEvent.duration);
    existingActivity.activityActor.userMappingID = mappingId;
    existingActivity.activityActor.availabilityID = existingActivity.activityActor.availabilityID;
    existingActivity.activityActor.actor.name = activityEvent.actor.name;
    existingActivity.activityActor.actor.type = actor.type;
    existingActivity.activityActor.actor.description = activityEvent.actor.description;

    existingActivity.activityActor.actor.meta = actor.meta;
    existingActivity.activityActor.actor.metaExpanded = actor.metaExpanded;
  }

  private createActivityFromActivityEvent(activityEvent: ActivityEvent, date: Date): Activity {
    let activity = new Activity({
      startLocationID: activityEvent.startLocation,
      endLocationID: activityEvent.endLocation,
      comment: activityEvent.comment,
      start: date,
      duration: DateTimeUtils.getDurationString(activityEvent.duration)
    });

    return activity;
  }

  private createActorFromActivityEvent(activityEvent: ActivityEvent): Actor {

    let newActor: Actor = new Actor(activityEvent.actor);
    newActor.comment = activityEvent.comment;
    newActor.type = activityEvent.type;
    let meta: ActorMeta = newActor.metaExpanded;
    meta.ToBeBilled = activityEvent.billed;
    newActor.metaExpanded = meta;

    return newActor;
  }

  private async unassignActivity(actor: Actor, activity: Activity) {

    // find the activity that is to be unassigned and remove it from daypilot collection to avoid waiting for signalr and prevent user to click on it
    // again before signalr comes back.
    let existing = this.schedulerUpper.control.events.find(f => f.id() === activity.id);
    if (existing) {
      this.schedulerUpper.control.events.remove(existing);
    }

    let result = await this.schedulerUpperService.unassingActivity(actor, activity);

    if (!result) {
      // something went wrong - add the activity again
      this.schedulerUpper.control.events.add(existing);
    }

  }

  private async updateActivity(activity: ActivityActor, newUserMappingId: string) {
    // If moving to a new resource, then call the reassign directly
    if (newUserMappingId) {
      this.reassignActivity(activity, newUserMappingId);
    } else {
      let updatedActivity = await this.schedulerUpperService.updateActivity(activity);
      if (updatedActivity === undefined) {
        return false;
      }

      return true;
    }
  }

  private async reassignActivity(activity: Activity, userMappingID: string) {

    let availability = this.workShiftService.findAvailabilityForUserMapping(userMappingID, activity.start);

    if (availability === undefined) {
      console.log("Cannot find availability for driver");
      return;
    }

    let newActivity = new Activity(activity);
    newActivity.userMappingID = availability.userMappingID;
    newActivity.availabilityID = availability.id;

    await this.schedulerUpperService.reAssignActivity(activity, newActivity);

  }

  // Method that applies the chosen filter(s)
  private applyFilter() {
    try {
      if (this.schedulerUpper.control) {
        if (this.schedulerUpper.control.rows) {
          this.schedulerUpper.control.rows.filter(this.settings.settings);
        }
        if (this.schedulerUpper.control.events) {
          this.schedulerUpper.control.events.filter(this.settings.settings);
        }
      }

      setTimeout(() => {
        this.heightChanged.emit(true)
      }, 300);
    } catch (error) {
      this.logger.logError(error);
    }
  }

  private canEventBeMovedToResource(event: ActivityActorEvent) {
    if (!event.resource) {
      return false;
    }

    // Can not move cancelled activities
    if (event.activityActor && event.activityActor.state == StateMachineEventEnum.Cancel) {
      return false;
    }


    for (let group of this.workShiftGroups) {
      if (group.type !== GroupType.Air && group.type !== GroupType.Standby) {
        continue;
      }

      for (let workShift of group.workShifts) {
        if (this.createResourceKey(group.type, workShift.userMappingID) === event.resource && workShift.isWorkingByTime(event.start.toDateLocal().getTime(), event.start.toDateLocal().getTime())) {
          return true;
        }
      }
    }
    return false;
  }

  private activityBgColor(activityActorEvent: ActivityActorEvent) {

    let state: string = activityActorEvent.activityActor.state;

    if (activityActorEvent.activityActor.actor.type == BusEnum.Transfer && state == StateMachineEventEnum.NoShow) {
      return ColorsEnum.NoShow;
    }


    let flightId: string = activityActorEvent.activityActor.actor.liveFlightID;
    let start: Date = activityActorEvent.activityActor.start;
    let actorMeta: ActorMeta = activityActorEvent.activityActor.actor.metaExpanded;

    if (actorMeta.CancelledByApi) {
      return ColorsEnum.CancelledByApi;
    }

    let liveFlightEvent = this.liveFlightEvents.find(c => c.liveFlight.id === flightId);
    if (liveFlightEvent && !liveFlightEvent.liveFlight.bus) {
      return ColorsEnum.CancelledByApi;
    }

    if (state == StateMachineEventEnum.Assign && start < new Date()) {
      return ColorsEnum.Late;
    }

    return this.getBackgroundColorForLiveFlight(liveFlightEvent);

  }

  private getBackgroundColorForLiveFlight(liveFlightEvent: LiveFlightEvent) {
    if (liveFlightEvent?.liveFlight?.isArrival) {
      return ColorsEnum.Arrival;
    } else {
      if (liveFlightEvent?.liveFlight.isRemoteGate) {
        return ColorsEnum.DepartureRemoteTerminal;
      }
      return ColorsEnum.Departure;
    }
  }

  private activityStatusColor(event: ActivityActorEvent) {

    let status = event.activityActor.state;
    let type = activityAbbreviations.find(x => x.state == status);

    return type ? type.color : ColorsEnum.Unknown;
  }

  private showError(message: string) {
    this.toastr.error(message);
  }

  private showInfo(message: string) {
    this.toastr.info(message);
  }
}

